import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { title: "", body: "", jurisdiction: "England and Wales", confirmations: "" };
const messageFor = (error) => typeof error?.response?.data?.detail === "string"
  ? error.response.data.detail : "Unable to load or save declarations. Please try again.";

export function AdminVerificationDeclarations() {
  const { user } = useAuth();
  const allowed = (user?.roles || []).some((role) => ["super_admin", "compliance_reviewer"].includes(role));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    api.get(allowed ? "/verification/declarations" : "/verification/declarations/current")
      .then(({ data }) => { if (mounted) setItems(allowed ? data : [data]); })
      .catch((e) => {
        if (mounted) {
          if (!allowed && e?.response?.status === 404) setItems([]);
          else setError(messageFor(e));
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [allowed]);
  const current = items.find((d) => d.status === "active" && !d.is_deleted && !d.is_demo_data && !d.is_test_data &&
    (Array.isArray(d.applicable_services) ? d.applicable_services.includes("verification") : d.applicable_services === "verification"));
  const review = (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    const body = {
      title: form.title.trim(), body: form.body.trim(), jurisdiction: form.jurisdiction.trim(),
      applicable_services: ["verification"],
      required_confirmations: form.confirmations.split("\n").map((v) => v.trim()).filter(Boolean),
    };
    if (!body.title || !body.body || !body.jurisdiction) {
      setError("Enter a title, declaration text and jurisdiction before previewing.");
      return;
    }
    setPreview(body);
  };
  const publish = async () => {
    if (!allowed || busy || !preview) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/verification/declarations", preview);
      setItems((previous) => [data, ...previous.map((d) => d.status === "active" ? { ...d, status: "superseded" } : d)]);
      setForm(EMPTY);
      setPreview(null);
      setMessage(`Declaration version ${data.version} published. Its recorded legal review status is solicitor review required. Existing customer acceptances retain their original version.`);
    } catch (e) { setError(messageFor(e)); }
    finally { setBusy(false); }
  };
  return <section className="mt-8" aria-label="Verification declarations">
    <h2 className="text-xl font-semibold">Verification declaration</h2>
    <p className="text-sm text-slate-600 mt-2">Customers must accept the current declaration before paying. Publishing a replacement keeps previous versions and customer acceptance records.</p>
    {loading ? <p className="mt-4" role="status">Loading declaration status…</p> : error ? null :
      <p className="mt-4" role="status">{current ? `Current verification declaration: ${current.title} · Version ${current.version}` : "No production verification declaration is published. Customers cannot complete declaration acceptance and payment yet."}</p>}
    {error && <p role="alert" className="mt-4 text-red-700">{error}</p>}
    {message && <p role="status" className="mt-4">{message}</p>}
    {!allowed && <p className="mt-3 text-sm">A compliance reviewer or super administrator can publish declaration versions. This account can view the current publication status.</p>}
    {allowed && !loading && !preview && <form className="card p-6 mt-5 space-y-4" onSubmit={review}>
      <h3 className="font-semibold">Prepare a new declaration version</h3>
      <p className="text-sm text-slate-600">Enter your reviewed business terms. Publication makes this version available to new applicants immediately and records solicitor review as required. It does not record legal approval.</p>
      <label className="block text-sm">Declaration title<input className="field mt-1" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
      <label className="block text-sm">Jurisdiction<input className="field mt-1" required value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} /></label>
      <label className="block text-sm">Full declaration text<textarea className="field mt-1" rows={12} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></label>
      <label className="block text-sm">Required customer confirmations (one per line)<textarea className="field mt-1" rows={4} value={form.confirmations} onChange={(e) => setForm({ ...form, confirmations: e.target.value })} /></label>
      <button className="btn btn-primary" type="submit">Preview declaration</button>
    </form>}
    {allowed && preview && <div className="card p-6 mt-5 space-y-4">
      <h3 className="font-semibold">Declaration preview</h3>
      <p>{preview.title} · {preview.jurisdiction}</p>
      <p className="whitespace-pre-wrap">{preview.body}</p>
      <ul className="list-disc pl-5">{preview.required_confirmations.map((text, index) => <li key={index}>{text}</li>)}</ul>
      <p className="text-sm">Publishing supersedes the current active declaration and makes this text available to new verification applicants. Existing signed versions remain unchanged.</p>
      <button className="btn btn-primary" disabled={busy} onClick={publish}>{busy ? "Publishing…" : "Publish declaration version"}</button>
      <button className="btn btn-ghost ml-3" disabled={busy} onClick={() => setPreview(null)}>Back to editing</button>
    </div>}
    {allowed && <div className="mt-5 space-y-3">{items.map((d) => <details key={d.id} className="card p-4">
      <summary>{d.title} · Version {d.version} · {d.status}{d.is_demo_data || d.is_test_data ? " · Demo/test" : ""}</summary>
      <p className="text-sm mt-3">{d.jurisdiction} · Legal review: {(d.legal_review_status || "not recorded").replace(/_/g, " ")}</p>
      <p className="whitespace-pre-wrap mt-3">{d.body}</p>
      <ul className="list-disc pl-5 mt-3">{(d.required_confirmations || []).map((text, index) => <li key={index}>{text}</li>)}</ul>
    </details>)}</div>}
  </section>;
}
